import React, { useEffect, useState } from 'react';

import { useLocale, useTranslations } from 'next-intl';
import { Descendant } from 'slate';

import QuantityInput from 'components/QuantityInput';
import usePeriodText from 'hooks/usePeriodText';
import { BillingPeriodType, BillingPeriodUnitType } from 'types/common';
import { PlanItemType, PlanProductType } from 'types/subscriptionPlans';
import ButtonWrapper from 'ui/ButtonWrapper';
import RichTextEditorContentRender from 'ui/RichTextEditorContentRender';
import SelectInput from 'ui/SelectInput';
import getCountryOptions from 'utils/getCountryOptions';
import { isMatchedIntroAndRecurringPricePeriod } from 'utils/isMatchedIntroAndRecurringPricePeriod';
import { isRichTextEditorEmpty } from 'utils/validateRichTextEditor';
import getFormattedDate from 'utils/getFormattedDate';

const OptionalProduct = ({
  introBillingPeriod,
  introBillingPeriodUnit,
  introBillingPeriodFrequency,
  product,
  plan,
  introPeriodFixedEndDate,
  onChangeQuantity,
  onChangeShippingCountries,
}: {
  introBillingPeriod: BillingPeriodType;
  introBillingPeriodUnit: BillingPeriodUnitType;
  introBillingPeriodFrequency: number;
  product: PlanProductType;
  plan: PlanItemType;
  introPeriodFixedEndDate: string;
  onChangeQuantity: (productId: number, quantity: number) => void;
  onChangeShippingCountries: (productId: number, shippingCountries: string[]) => void;
}) => {
  const tCheckout = useTranslations('checkout');
  const tCountries = useTranslations('countries');
  const tCommon = useTranslations('common');

  const locale = useLocale();

  const [quantity, setQuantity] = useState(product.properties?.isRequired ? 1 : 0);
  const [shippingCountries, setShippingCountries] = useState<string[]>([]);
  const [quantityError, setQuantityError] = useState('');
  const [showDescription, setShowDescription] = useState(false);
  const minQuantity = product.properties?.isRequired ? 1 : 0;

  useEffect(() => {
    setQuantity(product?.quantity || (product.properties?.isRequired ? 1 : 0));
    setShippingCountries(product?.shippingCountries || []);
  }, [product.properties?.isRequired, product?.quantity, product?.shippingCountries]);

  useEffect(() => {
    if (quantity < minQuantity || isNaN(quantity)) {
      setQuantityError(
        tCheckout('quantityError', {
          min: minQuantity,
        }),
      );
    } else {
      onChangeQuantity(product.productId, quantity);
    }
  }, [quantity, minQuantity]);

  useEffect(() => {
    onChangeShippingCountries(product.productId, shippingCountries);
  }, [shippingCountries]);

  const handleToggleDescription = () => {
    setShowDescription(!showDescription);
  };

  const introPeriodText = usePeriodText(
    introBillingPeriod as string,
    introBillingPeriodFrequency as number,
    introBillingPeriodUnit as string,
  );

  const recurrentPeriodText = usePeriodText(
    plan.billingPeriod,
    plan.billingPeriodFrequency as number,
    plan.billingPeriodUnit as string,
  );

  const handleCountryChange = (index: number, value: string) => {
    setShippingCountries(shippingCountries.map((country, i) => (i === index ? value : country)));
  };

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    setQuantity(parseInt(value));
  };

  const handleIncrementQuantity = () => {
    setQuantity(quantity + 1);
    if (product.productCategory.match('physical_subscription'))
      setShippingCountries([...shippingCountries, 'NO']);
  };

  const handleDecrementQuantity = () => {
    if (quantity > minQuantity) {
      setQuantity(quantity - 1);
      if (product.productCategory.match('physical_subscription'))
        setShippingCountries(shippingCountries.slice(0, -1));
    }
  };

  const isPricePeriodMatched = isMatchedIntroAndRecurringPricePeriod(
    product.price,
    product.recurringPrice || 0,
    introBillingPeriod,
    plan.billingPeriod,
    introBillingPeriodFrequency,
    plan.billingPeriodFrequency as number,
    introBillingPeriodUnit,
    plan.billingPeriodUnit,
  );

  const introPeriodTextConsideringFixedEndDate = introPeriodFixedEndDate
    ? tCheckout('introProductPriceWillBeEndOn', {
        price: product.price,
        date: getFormattedDate(new Date(introPeriodFixedEndDate), locale),
      })
    : tCheckout('optionalProductIntroPriceOnOFF', {
        period: introPeriodText,
        price: product.price,
      });

  const introPriceText =
    isPricePeriodMatched && plan.autoRenew
      ? tCheckout('optionalProductIntroPrice', {
          price: product.price,
          period: introPeriodText,
        })
      : introPeriodTextConsideringFixedEndDate;

  const recurringPriceText =
    isPricePeriodMatched || !plan.autoRenew
      ? ''
      : tCheckout('optionalProductRecurringPrice', {
          price: product.recurringPrice ?? 0,
          period: recurrentPeriodText,
        });

  const filteredCountryNames = product.shippingCharges.map(
    (shippingCharge) => shippingCharge.country,
  );

  const countryOptions = getCountryOptions(filteredCountryNames);
  const translatedCountryOptions = countryOptions.map((country) => {
    return { value: country.value, label: tCountries(country.label) };
  });

  const getShippingChargeByCountry = (country: string) => {
    return product.shippingCharges.find((shippingCharge) => shippingCharge.country === country)
      ?.charge;
  };

  return (
    <div className="flex flex-col gap-y-2 bg-xxLight px-4 py-4 border border-dividerGrey sm:rounded-md sm:px-6 gap-x-4">
      {quantityError && <p className="text-center text-red font-medium mb-2">{quantityError}</p>}
      <div className="flex justify-between">
        <div className="flex flex-col word-break gap-y-2">
          <span className="text-sm text-outerSpace font-bold">{product.name.toUpperCase()}</span>
          {introPriceText && (
            <span className="text-sm text-black font-normal">{introPriceText}</span>
          )}
          {recurringPriceText && (
            <span className="text-sm text-black font-normal">{recurringPriceText}</span>
          )}
        </div>

        {product.properties?.quantityRange === 1 ? (
          <div className="w-[105px] h-[38px] relative flex justify-end">
            {quantity === 0 ? (
              <ButtonWrapper type="button" theme="outline" onClick={handleIncrementQuantity}>
                {tCommon('add')}
              </ButtonWrapper>
            ) : (
              <ButtonWrapper
                type="button"
                theme="outline"
                disabled={product.properties?.isRequired}
                onClick={handleDecrementQuantity}
              >
                {tCommon('remove')}
              </ButtonWrapper>
            )}
          </div>
        ) : (
          <QuantityInput
            quantity={quantity}
            minQuantity={minQuantity}
            isChangeLoading={false}
            quantityRange={product.properties?.quantityRange}
            handleDecrementQuantity={handleDecrementQuantity}
            handleIncrementQuantity={handleIncrementQuantity}
            handleQuantityChange={handleQuantityChange}
          />
        )}
      </div>

      {product.description && !isRichTextEditorEmpty(JSON.parse(product.description)) && (
        <div className="flex flex-col gap-y-2">
          {showDescription && (
            <span className="text-sm text-black font-normal">
              <RichTextEditorContentRender
                text={JSON.parse(product.description) as unknown as Descendant[]}
              />
            </span>
          )}
          <span
            className="text-sm text-okBlue font-normal underline cursor-pointer"
            onClick={handleToggleDescription}
          >
            {showDescription ? `${tCheckout('hide')} <` : `${tCheckout('show')} >`}
          </span>
        </div>
      )}
      {
        // TODO: We don't have indicator for a product has shipping charges enable or not
        // By default norway is selected for physical product as shipping charges
        product.shippingCharges.length > 1
          ? shippingCountries.map((country, index) => (
              <div key={index}>
                <span className="text-sm text-black font-normal">
                  {tCheckout('shippingCountryForProduct', { productName: product.name })}
                </span>
                <SelectInput
                  id={`shippingCountries${index}`}
                  name={`shippingCountries${index}`}
                  aria-label={tCheckout('shippingCountryForProduct', { productName: product.name })}
                  options={translatedCountryOptions}
                  value={shippingCountries[index]}
                  onChange={(event) => handleCountryChange(index, event.target.value)}
                />
                <span className="text-sm text-black font-normal">{`${tCheckout(
                  'shipping',
                )} NOK ${getShippingChargeByCountry(country)}`}</span>
              </div>
            ))
          : null
      }
    </div>
  );
};

export default OptionalProduct;
